import i18n, { resources } from '@/i18n';
import { Category } from '@/types';

/**
 * Category color mapping
 * Maps each category to its primary color
 */
export const CATEGORY_COLORS: Record<Category, string> = {
  [Category.ABSINTH]: '#7dac35',
  [Category.BOURBON]: '#8c5535',
  [Category.COGNAC]: '#b3571b',
  [Category.TEA]: '#c09161',
  [Category.CHOCOLATE]: '#4b2c20',
  [Category.FORTIFIED_WINES]: '#9c3d54',
  [Category.GIN]: '#4aa3a0',
  [Category.COFFEE]: '#523514',
  [Category.SPICES_AND_HERBS]: '#8f438b',
  [Category.LIQUEURS]: '#d9822b',
  [Category.MEAT]: '#b74141',
  [Category.MEZCAL]: '#386407ff',
  [Category.SEAFOOD]: '#2d7dd2',
  [Category.NONALCOHOLIC_DRINKS]: '#de8b8a',
  [Category.FRUIT_DESTILATES]: '#ffb347',
  [Category.BEER]: '#c58a07',
  [Category.RUM]: '#7d2e14',
  [Category.FISHES]: '#1b6ca8',
  [Category.CHEESE]: '#d9d996',
  [Category.TOBACCO]: '#6e4b1f',
  [Category.TEQUILA]: '#3ab795',
  [Category.WINE]: '#8a201a',
  [Category.VODKA]: '#6ec1e4',
  [Category.WHISKEY]: '#b26a00',
};

type CategoryTranslationKey = keyof typeof resources.en.translation.categories;

const CATEGORY_TRANSLATION_KEYS: Record<Category, CategoryTranslationKey> = {
  [Category.ABSINTH]: 'absinth',
  [Category.BOURBON]: 'bourbon',
  [Category.COGNAC]: 'cognac',
  [Category.TEA]: 'tea',
  [Category.CHOCOLATE]: 'chocolate',
  [Category.FORTIFIED_WINES]: 'fortifiedWines',
  [Category.GIN]: 'gin',
  [Category.COFFEE]: 'coffee',
  [Category.SPICES_AND_HERBS]: 'spicesHerbs',
  [Category.LIQUEURS]: 'liqueurs',
  [Category.MEAT]: 'meat',
  [Category.MEZCAL]: 'mezcal',
  [Category.SEAFOOD]: 'seafood',
  [Category.NONALCOHOLIC_DRINKS]: 'nonalcoholicDrinks',
  [Category.FRUIT_DESTILATES]: 'fruitDestilates',
  [Category.BEER]: 'beer',
  [Category.RUM]: 'rum',
  [Category.FISHES]: 'fishes',
  [Category.CHEESE]: 'cheese',
  [Category.TOBACCO]: 'tobacco',
  [Category.TEQUILA]: 'tequila',
  [Category.WINE]: 'wine',
  [Category.VODKA]: 'vodka',
  [Category.WHISKEY]: 'whiskey',
};

/**
 * Get color for a category
 */
export function getCategoryColor(category: Category): string {
  return CATEGORY_COLORS[category];
}

/**
 * Get label for a category
 */
export function getCategoryLabel(category: Category): string {
  const translationKey = `categories.${CATEGORY_TRANSLATION_KEYS[category]}` as const;
  return i18n.t(translationKey);
}

/**
 * Get primary color for a place based on its first category
 */
export function getPrimaryColor(categories: Category[]): string {
  return categories.length > 0 ? getCategoryColor(categories[0]) : '#95a5a6';
}

/**
 * Get secondary color for a place based on its second category
 */
export function getSecondaryColor(categories: Category[]): string | null {
  return categories.length > 1 ? getCategoryColor(categories[1]) : null;
}

/**
 * Create a dual-color marker icon data URL
 * This creates a circular marker with two colors split vertically
 */
export function createDualColorMarkerIcon(
  primaryColor: string,
  secondaryColor: string | null,
  size: number = 40
): string {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return '';
  }

  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 2;

  // Draw shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 2;

  if (secondaryColor) {
    // Draw left half with primary color
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, Math.PI / 2, (3 * Math.PI) / 2, false);
    ctx.closePath();
    ctx.fillStyle = primaryColor;
    ctx.fill();

    // Draw right half with secondary color
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, (3 * Math.PI) / 2, Math.PI / 2, false);
    ctx.closePath();
    ctx.fillStyle = secondaryColor;
    ctx.fill();

    // Draw border
    ctx.shadowColor = 'transparent';
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();
  } else {
    // Draw single color circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = primaryColor;
    ctx.fill();

    // Draw border
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  return canvas.toDataURL();
}
